import { Card, Col, Container, Row, Table } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useCaseQuery } from "../../queries";
import { Icon, LoadingSpinner } from "../../component-library";
import { DashboardMetric, MonthlyPlacementChart } from ".";
import { StatusCount, TerritoryStatusCount } from "../../models";
import { useMemo } from "react";
import { fas } from "@fortawesome/free-solid-svg-icons";

export function AdminDashboard() {
    const navigate = useNavigate();
    const { adminDashboardQuery } = useCaseQuery({ queryType: "adminDashboard" });
    const activeReferrals = adminDashboardQuery.data?.activeReferrals ?? [];
    const donorCounts = adminDashboardQuery.data?.donors ?? [];
    const tissueCounts = adminDashboardQuery.data?.tissues ?? [];
    const orderCounts = adminDashboardQuery.data?.orders ?? [];
    const incomingOmarCounts = adminDashboardQuery.data?.incomingOmars ?? [];
    const totalActiveReferrals = activeReferrals.reduce((acc, curr) => acc + curr.referralCount, 0);
    const currentMonthPlacements = adminDashboardQuery.data?.currentMonthPlacementCount ?? 0;
    const previousMonthPlacements = adminDashboardQuery.data?.previousMonthPlacementCount ?? 0;
    const monthlyPlacementGoal = 225;

    const handleDonorStatusClick = (data: StatusCount) => {
        if (data.listSlug) {
            navigate(`/lists/${data.listSlug}/cases`);
        }
    };

    const handleTissueStatusClick = (data: StatusCount) => {
        if (data.listSlug) {
            navigate(`/lists/${data.listSlug}/tissues`);
        }
    };

    const handleOrderStatusClick = (data: StatusCount) => {
        if (data.listSlug) {
            navigate(`/lists/${data.listSlug}/orders`);
        }
    };

    const handleIncomingOmarClick = (data: TerritoryStatusCount) => {
        if (data.listSlug) {
            navigate(`/lists/${data.listSlug}/territory/${data.territoryId}/tissues`);
        }
    };

    const getStatsFooter = (value: number) => {
        if (value > monthlyPlacementGoal) {
            const overage = value - monthlyPlacementGoal;
            return `${overage} ${overage === 1 ? "tissue" : "tissues"} over goal`;
        } else if (value < monthlyPlacementGoal) {
            const remaining = monthlyPlacementGoal - value;
            return `${remaining} ${remaining === 1 ? "tissue" : "tissues"} to go`;
        }
        return "Goal met";
    };

    if (adminDashboardQuery.isFetching) return <LoadingSpinner />;

    return (
        <Container>
            <h1>Admin Dashboard</h1>
            <Row className="gx-3">
                <Col xl={6} lg={12} md={12} className="d-flex flex-column">
                    <Row>
                        <Col className="mb-3">
                            <Card className="bg-primary text-white">
                                <Card.Header className="d-flex justify-content-between">
                                    <div>
                                        <Link to="/active-referrals" className="text-white">
                                            Active Referrals
                                        </Link>{" "}
                                        ({totalActiveReferrals})
                                    </div>

                                    <Link to="/dashboard/referral" className="text-white">
                                        Referral Dashboard
                                    </Link>
                                </Card.Header>
                                <Card.Body className="bg-canvas">
                                    <Row>
                                        {activeReferrals.map((data) => (
                                            <Col key={data.territory.id} xs={6} md={3}>
                                                <DashboardMetric
                                                    title={data.territory.name}
                                                    value={data.referralCount}
                                                    onClick={() =>
                                                        navigate(`/active-referrals?territoryId=${data.territory.id}`)
                                                    }
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="mb-3">
                            <Card className="bg-primary text-white">
                                <Card.Header className="d-flex justify-content-between">
                                    <span>Incoming</span>
                                    <Link to="/dashboard/recovery" className="text-white">
                                        Recovery Dashboard
                                    </Link>
                                </Card.Header>
                                <Card.Body className="bg-canvas">
                                    <Row>
                                        {incomingOmarCounts.map((data) => (
                                            <Col key={data.status} xs={6} md={3}>
                                                <DashboardMetric
                                                    title={data.status}
                                                    value={data.count}
                                                    onClick={() => handleIncomingOmarClick(data)}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="mb-3">
                            <Card className="bg-primary text-white">
                                <Card.Header className="d-flex justify-content-between">
                                    <span>Orders</span>
                                    <Link to="/dashboard/distribution" className="text-white">
                                        Distribution Dashboard
                                    </Link>
                                </Card.Header>
                                <Card.Body className="bg-canvas">
                                    <Row>
                                        {orderCounts.map((data) => (
                                            <Col key={data.status} xs={12} md={6}>
                                                <DashboardMetric
                                                    title={data.status}
                                                    value={data.count}
                                                    onClick={() => handleOrderStatusClick(data)}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
                <Col xl={6} md={12} className="d-flex flex-column">
                    <Row className="gx-3 d-flex align-items-stretch" style={{ flexGrow: 1 }}>
                        <Col xl={6} lg={6} sm={12} className="mb-3">
                            <Card className="bg-primary text-white h-100 w-100">
                                <Card.Header className="d-flex justify-content-between">
                                    Donors
                                    <div>
                                        <Link to="/dashboard/medical-chart" className="text-white">
                                            MCR
                                        </Link>{" "}
                                        |{" "}
                                        <Link to="/dashboard/quality-assurance" className="text-white">
                                            QA
                                        </Link>
                                    </div>
                                </Card.Header>
                                <Card.Body className="bg-canvas">
                                    <Table striped bordered hover>
                                        <tbody>
                                            {donorCounts.map((data) => (
                                                <tr
                                                    key={data.status}
                                                    tabIndex={0}
                                                    onClick={() => handleDonorStatusClick(data)}
                                                    className="clickable"
                                                >
                                                    <td className="pe-4">{data.status}</td>
                                                    <td className="text-center" style={{ minWidth: "50px" }}>
                                                        {data.count}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl={6} lg={6} sm={12} className="mb-3">
                            <Card className="bg-primary text-white h-100 w-100">
                                <Card.Header className="d-flex justify-content-between">
                                    Tissues
                                    <div>
                                        <Link to="/dashboard/lab" className="text-white">
                                            Lab
                                        </Link>{" "}
                                        |{" "}
                                        <Link to="/dashboard/processing" className="text-white">
                                            Processing
                                        </Link>
                                    </div>
                                </Card.Header>
                                <Card.Body className="bg-canvas">
                                    <Table striped bordered hover>
                                        <tbody>
                                            {tissueCounts.map((data) => (
                                                <tr
                                                    key={data.status}
                                                    tabIndex={0}
                                                    onClick={() => handleTissueStatusClick(data)}
                                                    className="clickable"
                                                >
                                                    <td className="pe-4">{data.status}</td>
                                                    <td className="text-center" style={{ minWidth: "50px" }}>
                                                        {data.count}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row style={{ flexGrow: 1 }}>
                        <Col className="mb-3 d-flex align-items-stretch">
                            <Card className="bg-primary text-white w-100 h-100">
                                <Card.Header>
                                    Monthly Tissue Placements
                                    <Icon
                                        icon={fas.faInfoCircle}
                                        className="ms-1"
                                        title="Full fee tissues placed according to donor date of death"
                                        aria-label="Full fee tissues placed according to donor date of death"
                                    />
                                </Card.Header>
                                <Card.Body className="bg-canvas text-primary">
                                    <Row>
                                        <Col className="d-flex align-items-center">
                                            <DashboardMetric
                                                title="Last Month"
                                                value={
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <div className="mx-4">{previousMonthPlacements}</div>

                                                        <MonthlyPlacementChart
                                                            value={previousMonthPlacements}
                                                            goal={monthlyPlacementGoal}
                                                        />
                                                    </div>
                                                }
                                                footer={getStatsFooter(previousMonthPlacements)}
                                                className="w-100"
                                            />
                                        </Col>
                                        <Col className="d-flex align-items-center">
                                            <DashboardMetric
                                                title="This Month"
                                                value={
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <div className="mx-4">{currentMonthPlacements}</div>
                                                        <MonthlyPlacementChart
                                                            value={currentMonthPlacements}
                                                            goal={monthlyPlacementGoal}
                                                        />
                                                    </div>
                                                }
                                                footer={getStatsFooter(currentMonthPlacements)}
                                                className="w-100"
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Container>
    );
}
